// 오른쪽 위 HUD 배치(2026-10-27 대표님 피드백 "오른쪽 위가 흩어져 있다 → 아이콘으로 정리, 미니맵은 일반 게임처럼 크게, 던전에서는 안 보이게").
// 온라인 모바일 RPG 구조: 맨 위 오른쪽에 작은 그림 아이콘 한 줄(자주 쓰는 가방·스킬·퀘스트 + 더보기 + 설정, 누르면 그 창이 바로 열림), 그 아래(또는 옆)에 큰 미니맵,
// 미니맵 아래 테두리에 지역 이름·시계 명판, 미니맵 왼쪽 아래에 작은 카메라 초기화 단추. 일시정지·전체 화면·시야 확대는 설정(톱니) 창 안으로 옮겼다.
// 던전은 메뉴 줄을 톱니 하나로 줄인다(가방·스킬·퀘스트·지도·일일·상점·영웅 창은 게임 규칙상 마을에서만 열리므로 던전에 두면 눌러도 안 되는 가짜 단추가 된다).
// 순수 함수(DOM 없음): 레이아웃이 바뀔 때만 부르고 고정 스텝·프레임 루프에서는 부르지 않는다.
import { hudRectsOverlap, protectedPlayRect, touchHudLayout, type HudRect } from './skillHudLayout';
import { marketEnabledHere, partyEnabledHere } from '../shared/content/social';
import { raidNetEnabledHere } from '../shared/content/raidNetFlags';

export type MenuId = 'hero' | 'bag' | 'skill' | 'stat' | 'set' | 'quest' | 'daily' | 'map' | 'shop' | 'settings' | 'more' | 'party' | 'mail' | 'market';
export type HudScene = 'village' | 'dungeon';

// 2026-10-27 18:54 대표님 추가 피드백 "아이콘 좀 더 축소해서 일렬로 두거나 필요한 것만 꺼내지게": 작은 한 줄 + 접기.
/** 늘 꺼내 두는 마을 메뉴(자주 여는 창: 가방·스킬·퀘스트) */
export const MENU_PRIMARY: readonly MenuId[] = ['bag', 'skill', 'quest'];
/** 「더보기」를 누르면 펼쳐지는 메뉴. 일일 게시판은 설정(톱니) 창 바로가기로 옮겼다(같은 피드백 "의뢰 게시판 같은 건 설정에") */
export const MENU_FOLDED: readonly MenuId[] = ['stat', 'set', 'map', 'shop', 'hero'];
/** 마을 메뉴 전체(접힘 순서와 무관한 시험·DOM 생성용) */
export const VILLAGE_MENU: readonly MenuId[] = [...MENU_FOLDED, ...MENU_PRIMARY, 'more', 'settings'];
/** 파티·우편함 단추(레인 D): 파티 창이 열린 곳(partyEnabledHere)에서만 접힌 세트 뒤에 붙는다. 꺼져 있으면 아래 함수들은 위 상수와 똑같은 값을 낸다 */
export const MENU_PARTY: readonly MenuId[] = ['party', 'mail'];
/** 「거래소」 단추: marketEnabledHere()일 때만(파티 단추와 같은 방식) */
function extraFolded(): readonly MenuId[] {
  // 레이드 넷만 열린 곳은 보상을 받을 우편함 단추만 붙는다(파티 단추는 파티 공개 때만)
  return [...(partyEnabledHere() ? MENU_PARTY : raidNetEnabledHere() ? (['mail'] as const) : []), ...(marketEnabledHere() ? (['market'] as const) : [])];
}
export function foldedMenu(): readonly MenuId[] { const x = extraFolded(); return x.length ? [...MENU_FOLDED, ...x] : MENU_FOLDED; }
export function villageMenu(): readonly MenuId[] { const x = extraFolded(); return x.length ? [...MENU_FOLDED, ...x, ...MENU_PRIMARY, 'more', 'settings'] : VILLAGE_MENU; }
/** 던전: 톱니(일시정지·설정·귀환서·마을로·전체 화면·카메라)만. 물약 칸은 아래쪽 퀵슬롯, 미니맵은 그대로 */
export const DUNGEON_MENU: readonly MenuId[] = ['settings'];
/** 장면·펼침 상태별로 보이는 단추(왼쪽→오른쪽). 펼치면 접혀 있던 단추가 꺼내 둔 단추 왼쪽(또는 아랫줄)에 붙는다 */
export function menuItemsFor(scene: HudScene, expanded = false): readonly MenuId[] {
  if (scene === 'dungeon') return DUNGEON_MENU;
  return expanded ? villageMenu() : [...MENU_PRIMARY, 'more', 'settings'];
}

/** 단추 이름(접근성 이름·툴팁)과 단축키 */
export const MENU_META: Readonly<Record<MenuId, { label: string; key: string; icon: string }>> = {
  hero: { label: '영웅', key: '', icon: 'hero' },
  bag: { label: '가방', key: 'B', icon: 'bag' },
  skill: { label: '스킬', key: 'N', icon: 'skill' },
  stat: { label: '스탯', key: 'C', icon: 'rank' },
  set: { label: '세트', key: '', icon: 'set' },
  quest: { label: '퀘스트', key: 'G', icon: 'quest' },
  daily: { label: '일일', key: '', icon: 'daily' },
  map: { label: '지도', key: 'M', icon: 'map' },
  shop: { label: '상점', key: '', icon: 'shop' },
  settings: { label: '설정', key: 'Esc', icon: 'settings' },
  more: { label: '더보기', key: '', icon: '' },
  party: { label: '파티', key: '', icon: '' },
  mail: { label: '우편함', key: '', icon: '' },
  market: { label: '거래소', key: '', icon: '' },
};

/** 단추 툴팁의 단축키: 키 배치에 따라 스킬은 K(PC RPG식)/N, 스탯 C는 PC RPG식에서만(액션식 C는 카메라 초기화) */
export function menuKeyOf(id: MenuId, preset: 'action' | 'rpg'): string {
  if (id === 'skill') return preset === 'rpg' ? 'K' : 'N';
  if (id === 'stat') return preset === 'rpg' ? 'C' : '';
  return MENU_META[id].key;
}

export interface HudMenuOpts {
  /** 터치 기기: 오른쪽 아래 엄지 호(모든 버튼 크기 설정)를 피한다 */
  readonly touch: boolean;
  /** 피할 다른 아래쪽 조작 사각형(시험용). 없으면 touch일 때 touchControlRects */
  readonly controls?: readonly HudRect[];
  /** 「더보기」를 펼쳤는가 */
  readonly expanded?: boolean;
}

export interface HudMenuLayout {
  /** 'stack' = 메뉴 줄 아래 미니맵, 'side' = 미니맵을 맨 위 오른쪽 구석에 두고 메뉴 줄은 그 왼쪽(낮은 가로 화면) */
  readonly mode: 'stack' | 'side';
  /** 펼친 단추가 첫 줄에 다 못 들어가 아랫줄로 내려갔는가 */
  readonly foldRow: boolean;
  readonly items: readonly MenuId[];
  /** items와 같은 순서의 단추 사각형(화면 좌표) */
  readonly buttons: readonly HudRect[];
  readonly size: number;
  /** 단추 아래 작은 이름표를 보일 만큼 큰가 */
  readonly labels: boolean;
  /** 골드 알약(마을만) */
  readonly gold: HudRect | null;
  readonly minimap: HudRect;
  /** 미니맵 아래 테두리에 걸친 지역 이름·시계 명판 */
  readonly plate: HudRect;
  /** 카메라 초기화 단추 */
  readonly camera: HudRect;
  /** 전체를 감싸는 사각형(다른 띠·배너가 피한다) */
  readonly bounds: HudRect;
}

const MARGIN = 8, TOP = 6, GAP = 4;
/** 명판이 미니맵 아래로 삐져나오는 길이 */
/** first30-mobile: 명판은 미니맵 상자와 겹치지 않고 바로 아래에 붙는다(PLATE_OVER 0). 폰 390x700에서 겹침 1056~1224px²였다 */
export const PLATE_H = 24, PLATE_OVER = 0;
/** 누름 영역 최소 한 변(px) — 이웃 단추와 겹치지 않게 단추 사이 간격을 이 값에서 단추 크기를 뺀 만큼 벌린다 */
export const HIT_MIN = 44;
/** 터치 버튼 크기 설정 범위(설정 > 터치 버튼 크기 0.7~1.5배). 어떤 크기여도 오른쪽 위 묶음이 엄지 호를 덮지 않게 모두 피한다 */
const TOUCH_SCALES = [0.7, 1, 1.25, 1.5] as const;

/**
 * 터치 엄지 호(또는 격자)의 모든 칸과 각성 게이지(모든 크기 설정). 장면과 무관하게 같은 사각형을 피한다(배치 편집기 고정 영역과 맞춘다).
 * 2026-10-28 rebirth 통합: main #211 엄지 고리가 폰 가로(844×390)에서 각성 게이지를 y≈98까지 올려, 그 자리 미니맵은 약 70px가 된다(v2 단독 때 128px).
 */
export function touchControlRects(w: number, h: number): HudRect[] {
  const out: HudRect[] = [];
  for (const k of TOUCH_SCALES) {
    const p = touchHudLayout(w, h, k);
    out.push(p.attack, p.dodge, p.awaken, p.page, p.gauge, ...p.potions, ...p.skills);
  }
  return out;
}

/** 둘째 줄 이하 단추 중 맨 왼쪽 단추의 바로 왼쪽 자리(없으면 빈 목록) */
function lowRowLeft(buttons: readonly HudRect[], cs: number): HudRect[] {
  let b: HudRect | null = null;
  for (const r of buttons) if (r.y > TOP && (!b || r.x < b.x)) b = r;
  return b ? [{ x: b.x - cs - GAP, y: b.y + (b.h - cs) / 2, w: cs, h: cs }] : [];
}

/** x 범위 [x0, x1)에 걸친 조작 중 가장 위쪽 y(없으면 화면 높이) */
function floorAt(controls: readonly HudRect[], x0: number, x1: number, h: number): number {
  let y = h;
  for (const c of controls) if (c.x < x1 && c.x + c.w > x0 && c.y < y) y = c.y;
  return y;
}

/** 단추 크기(18:54 피드백 "더 축소"): 폰(짧은 변 500 미만) 34px, PC는 화면 높이 5.6%(720에서 40)·38~44. 터치 누름 영역은 CSS로 위아래를 넓힌다(menuBar.ts) */
export function menuButtonSize(w: number, h: number): number {
  if (Math.min(w, h) < 500) return 34;
  return Math.round(Math.max(38, Math.min(44, h * 0.056)));
}

/** 미니맵 희망 크기: 가로 화면 높이의 25%(720에서 180, 1080에서 220까지), 낮은 화면은 36%(390에서 140), 최소 120 */
function minimapPref(w: number, h: number): number {
  // 낮은 가로 폰(390)도 140까지: 예전 62px 링은 "일반 게임처럼 크게"에 못 미쳤다
  return Math.round(Math.max(120, Math.min(220, Math.max(h * 0.25, Math.min(h * 0.36, 150)), w * 0.3)));
}

interface Rows { buttons: HudRect[]; gold: HudRect | null; bottom: number; foldRow: boolean }

/**
 * 한 줄(오른쪽 정렬): [골드] [펼친 단추] [가방·스킬·퀘스트] [더보기] [설정]. 펼친 단추가 첫 줄에 다 들어가지 않으면 그 아래 작은 줄(xMax2 기준 오른쪽 정렬)로 내린다.
 * 첫 줄 높이까지 올라온 아래쪽 조작(작은 폰의 터치 격자)이 있으면 그 왼쪽에서 끝낸다.
 */
function placeRows(items: readonly MenuId[], xMax: number, xMax2: number, w: number, size: number, goldWant: number, controls: readonly HudRect[]): Rows {
  let goldW = goldWant;
  const step = Math.max(size + GAP, HIT_MIN);
  const buttons: HudRect[] = new Array(items.length);
  const fold = foldedMenu();
  const folded = items.filter((i) => fold.includes(i));
  const fixed = items.filter((i) => !fold.includes(i));
  let rowMax = xMax;
  for (const c of controls) if (c.y < TOP + size + 6 && c.y + c.h > TOP) rowMax = Math.min(rowMax, c.x - GAP * 2);
  const xMin = w / 3 + MARGIN;
  const put = (ids: readonly MenuId[], right: number, y: number): number => {
    let x = right - (ids.length - 1) * step - size; // 맨 오른쪽 단추는 오른쪽 끝에 붙인다
    const left = x;
    for (const id of ids) { buttons[items.indexOf(id)] = { x, y, w: size, h: size }; x += step; }
    return left;
  };
  const gh = Math.min(size, 40);
  // 44px 걸음으로 단추 줄이 넓어져도 골드가 첫 줄에 남도록, 모자라면 골드 알약을 84px까지 줄인다
  if (goldW > 0) goldW = Math.max(Math.min(goldW, 84), Math.min(goldW, rowMax - xMin + GAP - fixed.length * step - GAP));
  const goldFits = goldW === 0 || fixed.length * step + goldW + GAP <= rowMax - xMin + GAP;
  const room = rowMax - xMin + GAP - (goldW > 0 && goldFits ? goldW + GAP : 0);
  const inline = goldFits && (fixed.length + folded.length) * step <= room;
  const left = put(inline ? [...folded, ...fixed] : fixed, rowMax, TOP);
  let gold: HudRect | null = null;
  let y2 = TOP + step; // 세로 걸음도 누름 영역(HIT_MIN) 이상으로 벌려 윗줄·아랫줄 누름 영역이 겹치지 않게 한다(폰 34px 단추: 38 → 44)
  if (goldW > 0 && goldFits) gold = { x: left - GAP - goldW, y: TOP + (size - gh) / 2, w: goldW, h: gh };
  else if (goldW > 0) {
    // 좁은 세로 화면: 골드는 둘째 줄 오른쪽 끝, 펼친 단추는 그 아래 줄
    const gw = Math.min(goldW, xMax2 - xMin);
    const gy = TOP + size + GAP; // 골드 알약은 누름 단추가 아니라 예전 간격 그대로(셋째 줄이 112px 띠 안에 들어가야 한다)
    gold = { x: xMax2 - gw, y: gy + (size - gh) / 2, w: gw, h: gh };
    y2 = gy + step;
  }
  let bottom = gold ? Math.max(TOP + size, gold.y + gold.h) : TOP + size;
  // 112px 띠 아래로 내려가면 가운데 보호 영역에 걸리므로 띠 안에 붙여 둔다(좁은 세로 화면의 셋째 줄)
  if (y2 + size > 112) y2 = Math.max(bottom, 112 - size);
  if (!inline && folded.length) { put(folded, xMax2, y2); bottom = y2 + size; }
  return { buttons, gold, bottom, foldRow: !inline && folded.length > 0 };
}

/**
 * w·h 화면(게임 화면 좌표), scene 장면. 낮은 가로 화면(폰)에서는 미니맵을 구석에 두고 메뉴 줄을 그 왼쪽으로(side),
 * 넉넉한 화면은 메뉴 줄 아래 큰 미니맵(stack). 둘 중 미니맵이 크게 나오는 쪽을 고른다(stack 우선).
 */
export function hudMenuLayout(w: number, h: number, scene: HudScene, opts: HudMenuOpts): HudMenuLayout {
  const items = menuItemsFor(scene, opts.expanded === true);
  const size = menuButtonSize(w, h);
  const labels = false; // 작은 한 줄: 이름은 툴팁·접근성 이름으로만
  const goldW = scene === 'village' ? Math.round(Math.max(124, Math.min(160, size * 2.9))) : 0;
  const controls = opts.controls ?? (opts.touch ? touchControlRects(w, h) : []);
  const pref = minimapPref(w, h);
  const xR = w - MARGIN;
  /** 미니맵이 [x0, xR] 열에서 top부터 차지할 수 있는 크기(명판·카메라 단추·조작과의 간격 포함) */
  const fit = (top: number, maxW: number): number => {
    for (let m = Math.floor(Math.min(pref, maxW)); m >= 56; m -= 2) {
      const floor = floorAt(controls, xR - Math.max(m, 132) - 50, xR, h);
      if (top + m + (PLATE_H - PLATE_OVER) + 8 <= Math.min(floor, h - MARGIN)) return m;
    }
    return 0; // 이 자리에는 들어가지 않는다
  };
  // stack: 메뉴 첫 줄 아래(펼친 아랫줄은 미니맵 왼쪽에 붙으므로 미니맵 자리는 펼침과 무관 — 눌러도 미니맵이 움직이지 않는다)
  // 좁은 세로 화면(w < 600)은 골드·펼친 단추가 아랫줄로 내려가므로 그 줄까지 비워 둔 아래에 미니맵(펼쳐도 움직이지 않게 늘 같은 자리)
  const narrow = w < 600;
  const sTop = narrow && scene === 'village' ? TOP + 3 * (size + GAP) + 6 : TOP + size + 8;
  const sMap = fit(sTop, w / 3 - MARGIN * 2);
  // side: 미니맵을 구석에, 메뉴 줄은 그 왼쪽
  const dMap = fit(TOP, w / 3 - MARGIN * 2);
  const useStack = sMap > 0 && (sMap >= Math.min(pref, dMap) * 0.92 || sMap >= 150);
  const m = useStack ? sMap : Math.max(56, dMap);
  const minimap: HudRect = { x: xR - m, y: useStack ? sTop : TOP, w: m, h: m };
  // 명판: 미니맵 아래 테두리에 걸친다. 좁으면 미니맵보다 넓게(가운데 보호 영역 침범 없이)
  const pw = Math.max(m, Math.min(150, w / 3 - MARGIN * 2));
  const plate: HudRect = { x: Math.min(minimap.x + m / 2 - pw / 2, xR - pw), y: minimap.y + m - PLATE_OVER, w: pw, h: PLATE_H };
  // 펼친 아랫줄은 미니맵 왼쪽, 명판이 그 높이까지 올라오면(작은 폰) 명판 왼쪽에서 끝낸다
  let xMax2 = narrow ? xR : minimap.x - 8;
  if (!narrow && plate.y < TOP + size + Math.max(size + GAP, HIT_MIN) + GAP) xMax2 = Math.min(xMax2, plate.x - 8);
  const rows = placeRows(items, useStack ? xR : minimap.x - 8, xMax2, w, size, goldW, controls);
  // 카메라 초기화: 미니맵 왼쪽 아래 → 명판 왼쪽 → 명판 아래 순으로 빈 자리. 없으면 미니맵 원 왼쪽 아래 귀퉁이(원 바깥 빈 모서리)에 붙인다
  const taken: HudRect[] = [...rows.buttons, minimap, plate, ...(rows.gold ? [rows.gold] : [])];
  const prot = protectedPlayRect(w, h);
  // 왼쪽 1/3(초상·목표 카드 띠)에는 두지 않는다(C8-8: 폰 가로 조작이 낮아져 미니맵이 커지면 「골드 앞」 후보가 왼쪽 띠로 넘어갔다)
  const ok = (r: HudRect): boolean => r.x >= w / 3 && r.y >= 0 && r.x + r.w <= w &&
    r.y + r.h + 6 <= Math.min(floorAt(controls, r.x, r.x + r.w, h), h - MARGIN) &&
    !hudRectsOverlap(r, prot) && !taken.some((t) => hudRectsOverlap(r, { x: t.x - 2, y: t.y - 2, w: t.w + 4, h: t.h + 4 }));
  // 메뉴 단추와 같은 크기의 둥근 단추(한 덩어리로 보이게)
  const cs = size;
  const cx = Math.min(minimap.x, plate.x) - cs - 6;
  const cands: HudRect[] = [
    { x: minimap.x - cs - 6, y: minimap.y + m - cs - PLATE_OVER - 4, w: cs, h: cs },
    { x: cx, y: plate.y + PLATE_H - cs, w: cs, h: cs },
    { x: cx, y: plate.y, w: cs, h: cs },
    { x: plate.x, y: plate.y + PLATE_H + 6, w: cs, h: cs },
    // 작은 폰에서 펼친 줄이 미니맵 왼쪽을 채우면: 첫 줄 맨 왼쪽(골드 앞)
    ...(rows.gold && rows.gold.y < TOP + size ? [{ x: rows.gold.x - cs - GAP, y: TOP, w: cs, h: cs }] : []),
    // 펼친 아랫줄 맨 왼쪽 단추 바로 왼쪽(C8-8: 폰 가로 조작이 낮아져 미니맵이 커지면 첫 줄 왼쪽이 왼쪽 띠로 넘어간다)
    ...lowRowLeft(rows.buttons, cs),
  ];
  const camera: HudRect = cands.find(ok) ?? { x: minimap.x - 6, y: minimap.y + m - cs - PLATE_OVER - 2, w: cs, h: cs };
  const all = [...rows.buttons, minimap, plate, camera, ...(rows.gold ? [rows.gold] : [])];
  const x0 = Math.min(...all.map((r) => r.x)), y0 = Math.min(...all.map((r) => r.y));
  const x1 = Math.max(...all.map((r) => r.x + r.w)), y1 = Math.max(...all.map((r) => r.y + r.h));
  return {
    mode: useStack ? 'stack' : 'side', foldRow: rows.foldRow, items, buttons: rows.buttons, size, labels, gold: rows.gold,
    minimap, plate, camera, bounds: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 },
  };
}

/** 고정 HUD로 등록할 사각형(터치 배치 편집기가 덮지 못한다): 단추·골드·미니맵·명판·카메라 */
export function hudMenuRects(p: HudMenuLayout): HudRect[] {
  return [...p.buttons, p.minimap, p.plate, p.camera, ...(p.gold ? [p.gold] : [])];
}
